import { useCallback } from 'react';

// ========== 型定義 ==========
export interface User {
  user_id: string;
  display_name: string;
  created_at: string;
}

export interface UserProfileMemory {
  user_id: string;
  occupation?: string;
  hobbies?: string;
  region?: string;
  preferred_tone?: 'healing' | 'support' | 'playful' | 'balanced';
  favorite_topics?: string;
  birthday?: string;
  important_dates?: string;
  updated_at: string;
}

export interface UserRelationMemory {
  memory_id?: number;
  user_id: string;
  type: 'concern' | 'preference' | 'emotional_pattern' | 'relationship_note';
  content: string;
  importance: 'low' | 'medium' | 'high';
  source_message_id?: number;
  created_at: string;
  updated_at: string;
}

export interface UserEventMemory {
  event_id?: number;
  user_id: string;
  event_type: 'birthday' | 'work_event' | 'trip' | 'anniversary' | 'health' | 'custom';
  title: string;
  event_date?: string;
  reminder_enabled: boolean;
  reminder_timing?: 'morning' | 'day_before' | 'week_before';
  note?: string;
  created_at: string;
}

export interface UserChatSummary {
  summary_id?: number;
  user_id: string;
  summary_period: string;
  summary_text: string;
  mood_trend?: string;
  updated_at: string;
}

// ========== IndexedDB設定 ==========
const DB_NAME = 'ai_inochan_memory_db';
const DB_VERSION = 1;

const STORE_USERS = 'users';
const STORE_PROFILE = 'user_profile_memory';
const STORE_RELATION = 'user_relation_memory';
const STORE_EVENT = 'user_event_memory';
const STORE_SUMMARY = 'user_chat_summary';

function openMemoryDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // users テーブル
      if (!db.objectStoreNames.contains(STORE_USERS)) {
        const userStore = db.createObjectStore(STORE_USERS, { keyPath: 'user_id' });
        userStore.createIndex('display_name', 'display_name', { unique: false });
      }

      // user_profile_memory テーブル
      if (!db.objectStoreNames.contains(STORE_PROFILE)) {
        db.createObjectStore(STORE_PROFILE, { keyPath: 'user_id' });
      }

      // user_relation_memory テーブル
      if (!db.objectStoreNames.contains(STORE_RELATION)) {
        const relationStore = db.createObjectStore(STORE_RELATION, {
          keyPath: 'memory_id',
          autoIncrement: true,
        });
        relationStore.createIndex('user_id', 'user_id', { unique: false });
        relationStore.createIndex('type', 'type', { unique: false });
        relationStore.createIndex('importance', 'importance', { unique: false });
      }

      // user_event_memory テーブル
      if (!db.objectStoreNames.contains(STORE_EVENT)) {
        const eventStore = db.createObjectStore(STORE_EVENT, {
          keyPath: 'event_id',
          autoIncrement: true,
        });
        eventStore.createIndex('user_id', 'user_id', { unique: false });
        eventStore.createIndex('event_date', 'event_date', { unique: false });
        eventStore.createIndex('event_type', 'event_type', { unique: false });
      }

      // user_chat_summary テーブル
      if (!db.objectStoreNames.contains(STORE_SUMMARY)) {
        const summaryStore = db.createObjectStore(STORE_SUMMARY, {
          keyPath: 'summary_id',
          autoIncrement: true,
        });
        summaryStore.createIndex('user_id', 'user_id', { unique: false });
        summaryStore.createIndex('summary_period', 'summary_period', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ========== CRUD操作 ==========

// --- Users ---
async function saveUser(user: User): Promise<void> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_USERS, 'readwrite');
    const store = tx.objectStore(STORE_USERS);
    const req = store.put(user);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function getUser(userId: string): Promise<User | undefined> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_USERS, 'readonly');
    const store = tx.objectStore(STORE_USERS);
    const req = store.get(userId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// --- Profile ---
async function saveProfile(profile: UserProfileMemory): Promise<void> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROFILE, 'readwrite');
    const store = tx.objectStore(STORE_PROFILE);
    const req = store.put(profile);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function getProfile(userId: string): Promise<UserProfileMemory | undefined> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROFILE, 'readonly');
    const store = tx.objectStore(STORE_PROFILE);
    const req = store.get(userId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// --- Relation Memory ---
async function saveRelationMemory(memory: UserRelationMemory): Promise<number> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_RELATION, 'readwrite');
    const store = tx.objectStore(STORE_RELATION);
    const req = store.add(memory);
    req.onsuccess = () => resolve(req.result as number);
    req.onerror = () => reject(req.error);
  });
}

async function getRelationMemories(userId: string): Promise<UserRelationMemory[]> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_RELATION, 'readonly');
    const store = tx.objectStore(STORE_RELATION);
    const index = store.index('user_id');
    const req = index.getAll(userId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getRelationMemoriesByType(userId: string, type: string): Promise<UserRelationMemory[]> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_RELATION, 'readonly');
    const store = tx.objectStore(STORE_RELATION);
    const index = store.index('user_id');
    const req = index.getAll(userId);
    req.onsuccess = () => {
      const filtered = req.result.filter((m: UserRelationMemory) => m.type === type);
      resolve(filtered);
    };
    req.onerror = () => reject(req.error);
  });
}

async function deleteRelationMemory(memoryId: number): Promise<void> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_RELATION, 'readwrite');
    const store = tx.objectStore(STORE_RELATION);
    const req = store.delete(memoryId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// --- Event Memory ---
async function saveEventMemory(event: UserEventMemory): Promise<number> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_EVENT, 'readwrite');
    const store = tx.objectStore(STORE_EVENT);
    const req = store.add(event);
    req.onsuccess = () => resolve(req.result as number);
    req.onerror = () => reject(req.error);
  });
}

async function getEventMemories(userId: string): Promise<UserEventMemory[]> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_EVENT, 'readonly');
    const store = tx.objectStore(STORE_EVENT);
    const index = store.index('user_id');
    const req = index.getAll(userId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getUpcomingEvents(userId: string, daysAhead: number): Promise<UserEventMemory[]> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_EVENT, 'readonly');
    const store = tx.objectStore(STORE_EVENT);
    const index = store.index('user_id');
    const req = index.getAll(userId);
    req.onsuccess = () => {
      const now = new Date();
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + daysAhead);
      
      const filtered = req.result.filter((e: UserEventMemory) => {
        if (!e.event_date) return false;
        const eventDate = new Date(e.event_date);
        return eventDate >= now && eventDate <= futureDate;
      });
      
      filtered.sort((a: UserEventMemory, b: UserEventMemory) => {
        const dateA = new Date(a.event_date || 0).getTime();
        const dateB = new Date(b.event_date || 0).getTime();
        return dateA - dateB;
      });
      
      resolve(filtered);
    };
    req.onerror = () => reject(req.error);
  });
}

async function deleteEventMemory(eventId: number): Promise<void> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_EVENT, 'readwrite');
    const store = tx.objectStore(STORE_EVENT);
    const req = store.delete(eventId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// --- Chat Summary ---
async function saveChatSummary(summary: UserChatSummary): Promise<number> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SUMMARY, 'readwrite');
    const store = tx.objectStore(STORE_SUMMARY);
    const req = store.add(summary);
    req.onsuccess = () => resolve(req.result as number);
    req.onerror = () => reject(req.error);
  });
}

async function getChatSummaries(userId: string): Promise<UserChatSummary[]> {
  const db = await openMemoryDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SUMMARY, 'readonly');
    const store = tx.objectStore(STORE_SUMMARY);
    const index = store.index('user_id');
    const req = index.getAll(userId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ========== React Hook ==========
export function useMemoryDB() {
  // ユーザー作成
  const createUser = useCallback(async (userId: string, displayName: string) => {
    const user: User = {
      user_id: userId,
      display_name: displayName,
      created_at: new Date().toISOString(),
    };
    await saveUser(user);
  }, []);

  // ユーザー取得
  const getUserData = useCallback(async (userId: string) => {
    return await getUser(userId);
  }, []);

  // ユーザー名更新
  const updateUserName = useCallback(async (userId: string, displayName: string) => {
    const user = await getUser(userId);
    if (user) {
      user.display_name = displayName;
      await saveUser(user);
    }
  }, []);

  // プロフィール取得
  const getProfileData = useCallback(async (userId: string) => {
    return await getProfile(userId);
  }, []);

  // プロフィール更新
  const updateProfile = useCallback(async (userId: string, profileData: Partial<UserProfileMemory>) => {
    const existing = await getProfile(userId);
    const newProfile: UserProfileMemory = {
      user_id: userId,
      occupation: profileData.occupation ?? existing?.occupation,
      hobbies: profileData.hobbies ?? existing?.hobbies,
      region: profileData.region ?? existing?.region,
      preferred_tone: profileData.preferred_tone ?? existing?.preferred_tone,
      favorite_topics: profileData.favorite_topics ?? existing?.favorite_topics,
      updated_at: new Date().toISOString(),
    };
    await saveProfile(newProfile);
  }, []);

  // 関係性記憶追加
  const addRelationMemory = useCallback(
    async (userId: string, memory: Omit<UserRelationMemory, 'memory_id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      const now = new Date().toISOString();
      const newMemory: UserRelationMemory = {
        user_id: userId,
        type: memory.type,
        content: memory.content,
        importance: memory.importance,
        source_message_id: memory.source_message_id,
        created_at: now,
        updated_at: now,
      };
      await saveRelationMemory(newMemory);
    },
    []
  );

  // 関係性記憶取得
  const getRelationMemoriesData = useCallback(async (userId: string) => {
    return await getRelationMemories(userId);
  }, []);

  // 関係性記憶取得（タイプ別）
  const getRecentRelationMemories = useCallback(async (userId: string, type: string, limit: number) => {
    const memories = await getRelationMemoriesByType(userId, type);
    return memories
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
  }, []);

  // 関係性記憶削除
  const removeRelationMemory = useCallback(async (memoryId: number) => {
    await deleteRelationMemory(memoryId);
  }, []);

  // イベント追加
  const addEvent = useCallback(
    async (userId: string, event: Omit<UserEventMemory, 'event_id' | 'user_id' | 'created_at'>) => {
      const newEvent: UserEventMemory = {
        user_id: userId,
        event_type: event.event_type,
        title: event.title,
        event_date: event.event_date,
        reminder_enabled: event.reminder_enabled,
        reminder_timing: event.reminder_timing,
        note: event.note,
        created_at: new Date().toISOString(),
      };
      await saveEventMemory(newEvent);
    },
    []
  );

  // イベント取得
  const getEvents = useCallback(async (userId: string) => {
    return await getEventMemories(userId);
  }, []);

  // 近日イベント取得
  const getUpcomingEventsData = useCallback(async (userId: string, daysAhead: number) => {
    return await getUpcomingEvents(userId, daysAhead);
  }, []);

  // イベント削除
  const removeEvent = useCallback(async (eventId: number) => {
    await deleteEventMemory(eventId);
  }, []);

  // チャット要約追加
  const addSummary = useCallback(
    async (userId: string, summary: Omit<UserChatSummary, 'summary_id' | 'user_id' | 'updated_at'>) => {
      const newSummary: UserChatSummary = {
        user_id: userId,
        summary_period: summary.summary_period,
        summary_text: summary.summary_text,
        mood_trend: summary.mood_trend,
        updated_at: new Date().toISOString(),
      };
      await saveChatSummary(newSummary);
    },
    []
  );

  // チャット要約取得
  const getSummaries = useCallback(async (userId: string) => {
    return await getChatSummaries(userId);
  }, []);

  // 全記憶を統合取得（useMemoryRetrieval/useMemoryWeaving用）
  const getMemories = useCallback(async (userId?: string): Promise<Array<{
    content: string;
    type: 'profile' | 'event' | 'relation';
    timestamp: number;
  }>> => {
    const uid = userId || localStorage.getItem('ai_inochan_user_id') || 'default_user';
    const [relations, events, profile] = await Promise.all([
      getRelationMemories(uid),
      getEventMemories(uid),
      getProfile(uid),
    ]);

    const result: Array<{ content: string; type: 'profile' | 'event' | 'relation'; timestamp: number }> = [];

    relations.forEach((m) => {
      result.push({
        content: m.content,
        type: 'relation',
        timestamp: new Date(m.created_at).getTime(),
      });
    });

    events.forEach((e) => {
      result.push({
        content: e.title + (e.note ? `（${e.note}）` : ''),
        type: 'event',
        timestamp: new Date(e.created_at).getTime(),
      });
    });

    if (profile) {
      if (profile.occupation) {
        result.push({
          content: `職業は${profile.occupation}`,
          type: 'profile',
          timestamp: new Date(profile.updated_at).getTime(),
        });
      }
      if (profile.hobbies && profile.hobbies.length > 0) {
        const hobbiesArray = Array.isArray(profile.hobbies)
          ? profile.hobbies
          : [profile.hobbies];
        result.push({
          content: `趣味は${hobbiesArray.join('、')}`,
          type: 'profile',
          timestamp: new Date(profile.updated_at).getTime(),
        });
      }
      if (profile.region) {
        result.push({
          content: `住んでいる地域は${profile.region}`,
          type: 'profile',
          timestamp: new Date(profile.updated_at).getTime(),
        });
      }
    }

    return result;
  }, []);

  return {
    createUser,
    getUser: getUserData,
    updateUserName,
    getProfile: getProfileData,
    updateProfile,
    addRelationMemory,
    getRelationMemories: getRelationMemoriesData,
    getRecentRelationMemories,
    removeRelationMemory,
    addEvent,
    getEvents,
    getUpcomingEvents: getUpcomingEventsData,
    removeEvent,
    addSummary,
    getSummaries,
    getMemories,
  };
}